import React, { useState } from 'react';
import { Row, Col } from 'react-flexbox-grid';
import Heading from '../UI/Texts/Heading';
import Button from '@material-ui/core/Button';
import Skeleton from '@mui/material/Skeleton';
import SelectorCantidad from '../Common/selectorCantidad/selectorCantidad';
import VisualizadorImagenesDetalle from './VisualizadorImagenesDetalle';
import DatosExtra, {
    getDatosExtraSegments,
    renderDatosExtraSegments,
    stripDatosExtraFormatting,
    truncateDatosExtraSegments
} from '../MostrarArticulos/datosExtra/datosExtra';
import ArticuloAlternativa from '../MostrarArticulos/articuloAlternativa/articuloAlternativa';
import ProductosRelacionadosCarousel from './ProductosRelacionadosCarousel';
import { formatMoney } from '../Common/decimalHelper';
import './styles.css';

const MAX_DESCRIPCION_LINEAS = 7;
const DESCRIPCION_SUFFIX = '... Ver más';

const truncateAtWordBoundary = (text, maxLength) => {
    const fragment = text.slice(0, maxLength).trimEnd();
    const lastWhitespace = fragment.search(/\s+[^\s]*$/);

    if (lastWhitespace > 0) {
        return fragment.slice(0, lastWhitespace).trimEnd();
    }

    return fragment;
};

const ProductoDetalleComponent = ({
    articulo,
    instalacion,
    usuario,
    configVidriera,
    cantidadEnCarrito,
    handleAgregarQuitarUnidad,
    onChangeCantidad,
    showAlternativas,
    cantidadInicialAlternativas,
    handleCloseAlternativas,
    loading = false,
    productosRelacionados = [],
    loadingRelacionados = false
}) => {
    const [imgOfertaError, setImgOfertaError] = useState(false);
    const [descripcionExpandida, setDescripcionExpandida] = useState(false);
    const [puedeExpandirDescripcion, setPuedeExpandirDescripcion] = useState(false);
    const [descripcionColapsadaLongitud, setDescripcionColapsadaLongitud] = useState(0);
    const descripcionRef = React.useRef(null);
    const descripcionMedidorRef = React.useRef(null);
    const descripcionSegmentos = React.useMemo(
        () => getDatosExtraSegments(articulo && articulo.datosExtra),
        [articulo && articulo.datosExtra]
    );

    React.useEffect(() => {
        setDescripcionExpandida(false);
        setPuedeExpandirDescripcion(false);
        setDescripcionColapsadaLongitud(0);
    }, [articulo && articulo.idArticulo, articulo && articulo.datosExtra]);

    React.useLayoutEffect(() => {
        if (!articulo || !articulo.datosExtra || descripcionExpandida) {
            return undefined;
        }

        const descripcionElemento = descripcionRef.current;
        const medidorElemento = descripcionMedidorRef.current;

        if (!descripcionElemento || !medidorElemento) {
            return undefined;
        }

        const plainText = stripDatosExtraFormatting(articulo.datosExtra).trim();

        const recalculateDescripcion = () => {
            if (!descripcionElemento || !medidorElemento) {
                return;
            }

            const width = descripcionElemento.clientWidth;

            if (!width) {
                return;
            }

            medidorElemento.style.width = `${width}px`;

            const lineHeight = parseFloat(window.getComputedStyle(descripcionElemento).lineHeight);
            const maxHeight = lineHeight * MAX_DESCRIPCION_LINEAS;

            const fits = (text) => {
                medidorElemento.textContent = text;
                return medidorElemento.scrollHeight <= maxHeight;
            };

            if (fits(plainText)) {
                setDescripcionColapsadaLongitud(plainText.length);
                setPuedeExpandirDescripcion(false);
                return;
            }

            let low = 0;
            let high = plainText.length;
            let best = '';

            while (low <= high) {
                const mid = Math.floor((low + high) / 2);
                const candidate = truncateAtWordBoundary(plainText, mid);
                const measuredText = `${candidate}${DESCRIPCION_SUFFIX}`;

                if (candidate && fits(measuredText)) {
                    best = candidate;
                    low = mid + 1;
                } else {
                    high = mid - 1;
                }
            }

            const fallbackText = truncateAtWordBoundary(plainText, Math.max(0, high));
            setDescripcionColapsadaLongitud((best || fallbackText).trimEnd().length);
            setPuedeExpandirDescripcion(true);
        };

        recalculateDescripcion();
        window.addEventListener('resize', recalculateDescripcion);

        return () => {
            window.removeEventListener('resize', recalculateDescripcion);
        };
    }, [articulo, descripcionExpandida]);

    const handleAddClick = () => {
        const deltaIncremento = articulo.incremento !== null && articulo.incremento !== undefined 
            ? articulo.incremento 
            : (articulo.productoPesable ? 0.25 : 1);
        
        const initialQuantity = articulo.cantidadLimMin && articulo.cantidadLimMin > 0 
            ? articulo.cantidadLimMin 
            : deltaIncremento;

        onChangeCantidad(initialQuantity);
    };

    const handleQuantityChange = (cantidad) => {
        onChangeCantidad(cantidad);
    };

    const toSentenceCase = (text) => {
        if (!text) return "";
        const lower = text.toLowerCase();
        return lower.charAt(0).toUpperCase() + lower.slice(1);
    };

    const descripcionColapsadaSegmentos = React.useMemo(
        () => truncateDatosExtraSegments(descripcionSegmentos, descripcionColapsadaLongitud),
        [descripcionColapsadaLongitud, descripcionSegmentos]
    );

    const breadcrumbSegments = [
        articulo.nombreRubro,
        articulo.nombreSubrubro,
        !articulo.nombreRubro && !articulo.nombreSubrubro ? articulo.nombreSeccion : null,
        articulo.nombre
    ].filter(Boolean);

    const breadcrumb = (
        <div className="breadcrumb">
            <a href="#/" className="inicio-bold">Inicio</a>
            {breadcrumbSegments.map((segmento) => ` > ${segmento}`)}
        </div>
    );

    const porcentajeDescuentoBackend = Number(articulo.porcentajeDescuento);
    const porcentajeDescuento = Number.isFinite(porcentajeDescuentoBackend) && porcentajeDescuentoBackend !== 0
        ? Math.abs(porcentajeDescuentoBackend)
        : null;

    const renderPrecio = () => {
        const principal = articulo.precioPromocional || articulo.precioLista || 0;
        const mostrarAntes = articulo.precioPromocional && articulo.precioLista > articulo.precioPromocional;
        
        const integerPart = Math.floor(principal);
        const decimalPart = (principal % 1).toFixed(2).substring(2);
        
        return (
            <div className="customPrecioContainer">
                {mostrarAntes && (
                    <div className="precioAnterior">
                        Antes <span>{formatMoney(articulo.precioLista)}</span>
                    </div>
                )}
                <div className="precioPrincipal">
                    <span className="signo">$</span>
                    <span className="precioEntero">{integerPart.toLocaleString('es-AR')}</span>
                    {decimalPart !== '00' && <span className="precioDecimales">{decimalPart}</span>}
                </div>
                {articulo.precioSinImpuestos && (
                    <div className="precioImpuesto">
                        Precio s/imp. nacionales {formatMoney(articulo.precioSinImpuestos)}
                    </div>
                )}
            </div>
        );
    };




    return (
        <div className="productoDetalleContainer">
            {breadcrumb}
            <Row className="productoDetalleRow">
                <Col xs={12} md={6} className="colImagen">
                    <div className="imageWrapper">
                        {loading ? (
                            <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
                        ) : (
                            <VisualizadorImagenesDetalle 
                                articulo={articulo}
                                instalacion={instalacion}
                            />
                        )}
                    </div>
                </Col>
                <Col xs={12} md={6} className="colInfo">
                    <div className="infoWrapper">
                        {loading ? (
                            <>
                                <Skeleton variant="text" width="80%" height={40} animation="wave" style={{ marginBottom: 5 }} />
                                <div className="badgesContainer">
                                    <Skeleton variant="rectangular" width={60} height={30} animation="wave" />
                                    <Skeleton variant="rectangular" width={60} height={30} animation="wave" />
                                </div>
                                <Skeleton variant="rectangular" width="100%" height={100} animation="wave" style={{ marginBottom: 10 }} />
                                <div className="productoPrecios">
                                    <Skeleton variant="text" width="40%" height={30} animation="wave" />
                                    <Skeleton variant="text" width="60%" height={80} animation="wave" />
                                    <Skeleton variant="text" width="50%" height={20} animation="wave" />
                                </div>
                                <Skeleton variant="rectangular" width={150} height={45} animation="wave" />
                            </>
                        ) : (
                            <>
                                <Heading tag="h1" weight="bold" className="productoNombre">
                                    {toSentenceCase(articulo.nombre)}
                                </Heading>

                                <div className="badgesContainer">
                                    {articulo.srcImgOferta && (
                                        <div className='selloOfertaDetalle'>
                                            {!imgOfertaError && (
                                                <img 
                                                    alt="Oferta" 
                                                    src={articulo.srcImgOferta.startsWith('http') 
                                                        ? articulo.srcImgOferta 
                                                        : `/${articulo.srcImgOferta.replace(/\\/g, '/')}`
                                                    } 
                                                    onError={() => setImgOfertaError(true)}
                                                />
                                            )}
                                        </div>
                                    )}
                                    {porcentajeDescuento > 0 && (
                                        <div className='labelDescuentoDetalle'>
                                            -{porcentajeDescuento.toFixed(0)}%
                                        </div>
                                    )}
                                </div>
                                    {articulo.datosExtra ? (
                                        <>
                                            {descripcionExpandida ? (
                                                <DatosExtra
                                                    datosExtra={articulo.datosExtra}
                                                    className="productoDescripcion productoDescripcionExpandida"
                                                    multiline
                                                    tooltip={false}
                                                >
                                                    <>
                                                        {' '}
                                                        <button
                                                            type="button"
                                                            className="productoDescripcionToggle"
                                                            onClick={() => setDescripcionExpandida(false)}
                                                        >
                                                            Ver menos
                                                        </button>
                                                    </>
                                                </DatosExtra>
                                            ) : (
                                                <div
                                                    ref={descripcionRef}
                                                    className="productoDescripcion productoDescripcionColapsada"
                                                >
                                                    {renderDatosExtraSegments(descripcionColapsadaSegmentos, 'producto-descripcion-colapsada')}
                                                    {puedeExpandirDescripcion ? (
                                                        <>
                                                            ...{' '}
                                                            <button
                                                                type="button"
                                                                className="productoDescripcionToggle"
                                                                onClick={() => setDescripcionExpandida(true)}
                                                            >
                                                                Ver más
                                                            </button>
                                                        </>
                                                    ) : null}
                                                </div>
                                            )}
                                            <div
                                                ref={descripcionMedidorRef}
                                                className="productoDescripcion productoDescripcionMedidor"
                                                aria-hidden="true"
                                            />
                                        </>
                                    ) : null}

                                {((configVidriera === "2" || configVidriera === 2) || (usuario && usuario.id)) && (
                                    <div className="productoPrecios">
                                        {renderPrecio()}
                                    </div>
                                )}

                                <div className="productoAcciones">
                                    {((configVidriera === "1" || configVidriera === "0" || configVidriera === 1 || configVidriera === 0) && (!usuario || !usuario.id)) ? (
                                            <Button 
                                                variant="contained" 
                                                className="btnAgregar"
                                                style={{ 
                                                    backgroundColor: 'var(--online-button-primary)', 
                                                    color: 'white', 
                                                    borderRadius: '50px',
                                                    textTransform: 'none',
                                                    height: '48px',
                                                    width: '100%',
                                                    fontWeight: 'bold'
                                                }}
                                                onClick={() => { window.location.hash = "/Login"; }}
                                            >
                                                Ver precio
                                            </Button>
                                    ) : articulo.catalogoEntradaActivo ? (
                                        cantidadEnCarrito === 0 ? (
                                                <Button 
                                                    variant="contained" 
                                                    className="btnAgregar"
                                                    style={{ 
                                                        backgroundColor: 'var(--online-button-primary)', 
                                                        color: 'white', 
                                                        borderRadius: '50px',
                                                        textTransform: 'none',
                                                        height: '48px',
                                                        width: '100%',
                                                        fontWeight: 'bold'
                                                    }}
                                                    onClick={handleAddClick}
                                                >
                                                        Agregar al carrito
                                                </Button>
                                        ) : (
                                            <SelectorCantidad
                                                value={cantidadEnCarrito}
                                                onChange={handleQuantityChange}
                                                incremento={articulo.incremento}
                                                productoPesable={articulo.productoPesable}
                                                cantidadLimMin={articulo.cantidadLimMin}
                                                cantidadLimMax={articulo.cantidadLimMax}
                                                stockDisponible={true}
                                            />
                                        )
                                    ) : (
                                        <div className="noStock">SIN STOCK</div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </Col>
                
                {!loading && (
                    <Col xs={12}>
                        {loadingRelacionados ? (
                            <div className="productosRelacionadosSkeleton">
                                <Skeleton variant="text" width="260px" height={36} animation="wave" style={{ marginBottom: 20 }} />
                                <div className="productosRelacionadosSkeletonGrid">
                                    {[0, 1, 2, 3].map((index) => (
                                        <div key={index} className="productosRelacionadosSkeletonCard">
                                            <Skeleton variant="rectangular" width="100%" height={180} animation="wave" />
                                            <Skeleton variant="text" width="90%" height={24} animation="wave" style={{ marginTop: 14 }} />
                                            <Skeleton variant="text" width="55%" height={20} animation="wave" />
                                            <Skeleton variant="text" width="70%" height={28} animation="wave" style={{ marginTop: 8 }} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <ProductosRelacionadosCarousel 
                                productosAsociados={productosRelacionados}
                                instalacion={instalacion}
                                usuario={usuario}
                                configVidriera={configVidriera}
                                agregarQuitarUnidad={handleAgregarQuitarUnidad}
                                mostrarPrecioEnRelacionados={true}
                            />
                        )}
                    </Col>
                )}
            </Row>

            {showAlternativas && !loading && (
                <ArticuloAlternativa
                    open={showAlternativas}
                    item={articulo}
                    initialValue={cantidadInicialAlternativas}
                    onClose={handleCloseAlternativas}
                    onConfirm={(cantidad, alternativa) => handleAgregarQuitarUnidad(cantidad, articulo, alternativa)}
                />
            )}
        </div>
    );
};

export default ProductoDetalleComponent;
